import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";

import {
  createDevelopmentWorkSummary,
  getAllDevelopmentWorkSummary,
  updateDevelopmentWorkSummary,
  deleteDevelopmentWorkSummary,
} from "../api/User";

/* ---------- BLOCK INVALID NUMBER INPUT ---------- */
const blockInvalidNumberInput = (e) => {
  if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

/* ---------- DEFAULT ROW ---------- */
const emptyRow = {
  id: null,
  totalWorks: "",
  totalFundReceived: "",
  totalFundUsed: "",
  completedWorks: "",
  ongoingWorks: "",
  notStartedWorks: "",
  remarks: "",
};

/* ---------- TRANSLATIONS ---------- */
const translations = {
  en: {
    back: "← Back",
    downloadPdf: "Download PDF",
    title: "Development Work (2025-26)",
    langToggle: "हिन्दी",
    freezeNotice: "This form has been edited 1 time and is now completely frozen. Changes cannot be made again.",
    colSrNo: "Sr. No.",
    col1: "Total number of development works executed under all schemes within 2025-26 *",
    col2: "Total funds received/combined during 2025-26 for these works *",
    col3: "Total funds used for these works during 2025-26 *",
    col4: "Number of completed works *",
    col5: "Number of ongoing works *",
    col6: "Number of not started works *",
    col7: "Gram Sabha Members Remarks *",
    colAction: "Action",
    edit: "Edit",
    frozen: "Frozen",
    frozenTooltip: "This record has been updated 1 time and is now completely frozen",
    delete: "Delete",
    submit: "Submit",
    update: "Update",
    cancel: "Cancel",
    confirmSaveTitle: "Save Changes",
    confirmUpdateTitle: "Confirm Update",
    confirmSaveMsg: "After saving you will be allowed to edit only once. Do you want to proceed?",
    confirmUpdateMsg: "After saving this time, you will not be allowed to edit this form again. Do you want to proceed?",
    yesSave: "Yes, Save",
    yesUpdate: "Yes, Update",
    maxDigitWarning: "Please note: You can enter a maximum of 10 digits only",
    validRangeWarning: "Please fill all values in valid range",
    mandatoryFieldsWarning: "Please fill all mandatory (*) fields",
    sumMismatchWarning: "The sum of completed, ongoing, and not started works must equal total works",
    fundExceedWarning: "Utilized funds cannot exceed received funds",
    deleteConfirmTitle: "Do you want to delete data?",
    deleteConfirmMsg: "This action cannot be undone",
    yesDelete: "Yes, Delete",
    recordDeletedSuccess: "Record deleted successfully",
    deleteFailed: "Delete failed",
    savedSuccessfully: "Saved successfully!",
    savedSuccessMsg: "After saving you will be allowed to edit only once.",
    updatedSuccessfully: "Form fully frozen!",
    updatedSuccessMsg: "After saving this time, you will not be allowed to edit this form again.",
    okay: "Okay",
    submitFailed: "Submit failed",
    noRecordFound: "Record not available",
  },
  hi: {
    back: "← वापस जाएँ",
    downloadPdf: "PDF डाउनलोड करें",
    title: "विकास कार्य (2025-26)",
    langToggle: "English",
    freezeNotice: "यह फॉर्म 1 बार संपादित हो चुका है और अब पूरी तरह से फ्रिज्ड (Freeze) है। इसमें दोबारा बदलाव नहीं किए जा सकते।",
    colSrNo: "क्र. सं.",
    col1: "सभी योजनाओं के तहत 2025-26 के भीतर निष्पादित विकास कार्यों की कुल संख्या *",
    col2: "इन कार्यों के लिए 2025-26 के दौरान कुल प्राप्त/संयोजित धनराशि *",
    col3: "इन कार्यों पर 2025-26 के दौरान कुल धनराशि का उपयोग किया गया *",
    col4: "पूर्ण किए गए कार्यों की संख्या *",
    col5: "चल रहे कार्यों की संख्या *",
    col6: "शुरू नहीं किए गए कार्यों की संख्या *",
    col7: "ग्राम सभा के सदस्यों की टिप्पणियां *",
    colAction: "कार्यवाही",
    edit: "संपादन",
    frozen: "फ्रिज्ड (Freeze)",
    frozenTooltip: "यह रिकॉर्ड 1 बार अपडेट किया जा चुका है और अब पूरी तरह से फ्रिज्ड (Freeze) है",
    delete: "हटाएँ",
    submit: "जमा करें",
    update: "अपडेट करें",
    cancel: "रद्द करें",
    confirmSaveTitle: "बदलाव सहेजें",
    confirmUpdateTitle: "अपडेट की पुष्टि करें",
    confirmSaveMsg: "सहेजने के बाद आपको केवल एक बार एडिट करने की अनुमति होगी। क्या आप आगे बढ़ना चाहते हैं?",
    confirmUpdateMsg: "इस बार सहेजने के बाद आपको इस फॉर्म को दोबारा एडिट करने की अनुमति नहीं होगी। क्या आप आगे बढ़ना चाहते हैं?",
    yesSave: "हाँ, सहेजें",
    yesUpdate: "हाँ, अपडेट करें",
    maxDigitWarning: "कृपया ध्यान दें: आप केवल अधिकतम 10 अंक ही दर्ज कर सकते हैं",
    validRangeWarning: "कृपया सभी मान सही रेंज में भरें",
    mandatoryFieldsWarning: "कृपया सभी अनिवार्य (*) फ़ील्ड भरें",
    sumMismatchWarning: "पूर्ण, चल रहे, और शुरू न हुए कार्यों की संख्या का योग कुल कार्यों के बराबर होना चाहिए",
    fundExceedWarning: "उपयोग की गई धनराशि, प्राप्त धनराशि से अधिक नहीं हो सकती",
    deleteConfirmTitle: "क्या आप डेटा हटाना चाहते हैं?",
    deleteConfirmMsg: "यह क्रिया पूर्ववत नहीं की जा सकती",
    yesDelete: "हाँ, हटाएं",
    recordDeletedSuccess: "रिकॉर्ड सफलतापूर्वक हटाया गया",
    deleteFailed: "हटाना विफल",
    savedSuccessfully: "सफलतापूर्वक सहेजा गया!",
    savedSuccessMsg: "सहेजने के बाद आपको केवल एक बार एडिट करने की अनुमति होगी।",
    updatedSuccessfully: "फॉर्म पूरी तरह फ्रिज (Freeze) हो गया!",
    updatedSuccessMsg: "इस बार सहेजने के बाद आपको इस फॉर्म को दोबारा एडिट करने की अनुमति नहीं होगी।",
    okay: "ठीक है",
    submitFailed: "सबमिट विफल",
    noRecordFound: "रिकॉर्ड उपलब्ध नहीं है",
  }
};

/* ---------- MAX VALUE VALIDATION ---------- */
const MAX_VALUE = 2147483647; // 2,14,74,83,647

const DevelopmentWork = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const token = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user_id");

  const [lang, setLang] = useState("en"); // Default English
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmType, setConfirmType] = useState("save"); // "save" or "update"
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState("");
  const [fieldExceedsMax, setFieldExceedsMax] = useState({}); // Track which fields exceed max value

  const [fetchedDistrictCode, setFetchedDistrictCode] = useState("");
  const [fetchedBlockCode, setFetchedBlockCode] = useState("");

  const getMeetingData = () => {
    const fromState = location.state;
    if (fromState) return fromState;

    const fromStorage = sessionStorage.getItem("current_meeting");
    return fromStorage ? JSON.parse(fromStorage) : {};
  };

  const meetingData = getMeetingData();

  const district_code = meetingData.district_code || meetingData.district_id || meetingData.district || meetingData.dist_code || fetchedDistrictCode || sessionStorage.getItem("district_code") || "";
  const block_code = meetingData.block_code || meetingData.block_id || meetingData.block || meetingData.blk_code || fetchedBlockCode || sessionStorage.getItem("block_code") || "";
  const gram_panchayat_code = meetingData.gram_panchayat_code || meetingData.gram_panchayat_id || meetingData.panchayat_code || meetingData.panchayat_id || sessionStorage.getItem("gram_panchayat_code") || "";
  const meeting_id = meetingData.meeting_id || meetingData.sched_meet_id || meetingData.id || "";
  const panchayat_name = meetingData.panchayat_name || "";

  // Auto-dismiss warning after 3 seconds
  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        setWarning("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  /* ================= FETCH EXISTING DATA ================= */
  useEffect(() => {
    fetchDevelopmentWork();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    if (["1", "3", "7", "8"].includes(role)) {
      setIsFrozen(true);
    }
  }, []);

  const fetchDevelopmentWork = async () => {
    try {
      setLoading(true);
      const res = await getAllDevelopmentWorkSummary({
        page: 1,
        limit: 1000,
        panchayat_code: gram_panchayat_code,
        district_code: district_code,
        block_code: block_code,
        meeting_id: meeting_id,
        sched_meet_id: meeting_id,
      });

      if (res?.status === "success" && res?.data?.length > 0) {
        // Find record specifically belonging to this meeting_id
        const d = res.data.find(
          (item) =>
            (item.sched_meet_id && meeting_id && String(item.sched_meet_id) === String(meeting_id)) ||
            (item.meeting_id && meeting_id && String(item.meeting_id) === String(meeting_id))
        ) || (res.data.length === 1 && !res.data[0].sched_meet_id ? res.data[0] : null);

        if (d) {
          if (d.district_code) setFetchedDistrictCode(d.district_code);
          if (d.block_code) setFetchedBlockCode(d.block_code);

          setRows([
            {
              id: d.id,
              totalWorks: d.total_executed_works,
              totalFundReceived: d.total_received_amount,
              totalFundUsed: d.total_utilized_amount,
              completedWorks: d.completed_works,
              ongoingWorks: d.ongoing_works,
              notStartedWorks: d.not_started_works,
              remarks: d.gram_sabha_remarks,
            },
          ]);
          setIsSubmitted(true);

          // Check if record was already updated / edited once (frozen)
          const isLocalFrozen =
            localStorage.getItem(`dev_work_frozen_${d.id}`) === "true" ||
            localStorage.getItem(`dev_work_frozen_${gram_panchayat_code}_${meeting_id}`) === "true";

          const isBackendFrozen =
            d.is_edited === 1 ||
            d.is_edited === true ||
            d.is_edited === "1" ||
            d.is_freeze === 1 ||
            d.is_freeze === true ||
            d.is_freeze === "1";

          const isViewOnlyRole = ["1", "3", "7", "8"].includes(sessionStorage.getItem("role_id"));

          if (isViewOnlyRole || isLocalFrozen || isBackendFrozen) {
            setIsFrozen(true);
          } else {
            setIsFrozen(false);
          }

          // Check existing data for max value violations using correct API field names
          const exceedsCheck = {};
          const fieldMapping = {
            totalWorks: d.total_executed_works,
            totalFundReceived: d.total_received_amount,
            totalFundUsed: d.total_utilized_amount,
            completedWorks: d.completed_works,
            ongoingWorks: d.ongoing_works,
            notStartedWorks: d.not_started_works,
          };

          let hasExceeding = false;
          Object.entries(fieldMapping).forEach(([field, apiValue]) => {
            const value = apiValue?.toString() || "";
            const numericValue = parseInt(value.replace(/\D/g, "") || 0, 10);
            exceedsCheck[field] = numericValue > MAX_VALUE;
            if (exceedsCheck[field]) hasExceeding = true;
          });

          setFieldExceedsMax(exceedsCheck);
          if (hasExceeding) {
            setWarning(translations[lang].maxDigitWarning);
          } else {
            setWarning("");
          }
        } else {
          // No record for this specific meeting -> empty state
          setRows([{ ...emptyRow }]);
          setIsSubmitted(false);
          const isViewOnlyRole = ["1", "3", "7", "8"].includes(sessionStorage.getItem("role_id"));
          setIsFrozen(isViewOnlyRole);
          setWarning("");
          setFieldExceedsMax({});
        }
      } else {
        setRows([{ ...emptyRow }]);
        setIsSubmitted(false);
        const isViewOnlyRole = ["1", "3", "7", "8"].includes(sessionStorage.getItem("role_id"));
        setIsFrozen(isViewOnlyRole);
        setWarning("");
        setFieldExceedsMax({});
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    if (isSubmitted || isFrozen) return;

    // Allow only digits
    const numericValue = value.replace(/\D/g, "");

    // Check if value exceeds 10 digits OR exceeds MAX_VALUE
    const numValue = parseInt(numericValue || 0, 10);
    const exceedsMax = numericValue.length > 10 || numValue > MAX_VALUE;

    // Update field exceeds state
    setFieldExceedsMax((prev) => ({
      ...prev,
      [field]: exceedsMax,
    }));

    // Update warning message
    if (exceedsMax) {
      setWarning(translations[lang].maxDigitWarning);
    } else {
      // Check if ANY other field still has error
      const otherFieldsHaveError = Object.entries(fieldExceedsMax).some(
        ([key, val]) => key !== field && val === true
      );

      // Clear warning only if NO field has error
      if (!otherFieldsHaveError) {
        setWarning("");
      }
    }

    const updated = [...rows];
    updated[index][field] = numericValue;
    setRows(updated);
  };

  const handleBlur = (field, value) => {
    if (isSubmitted || isFrozen) return;

    if (value) {
      const numericValue = parseInt(value.replace(/\D/g, "") || 0, 10);
      const exceedsMax = numericValue > MAX_VALUE || value.length > 10;

      // Update field exceeds state
      setFieldExceedsMax((prev) => ({
        ...prev,
        [field]: exceedsMax,
      }));

      // Update warning message
      if (exceedsMax) {
        setWarning(translations[lang].maxDigitWarning);
      } else {
        // Check if ANY field still has error
        const anyFieldHasError = Object.values(fieldExceedsMax).some(
          (val) => val === true
        );

        // Clear warning only if NO field has error
        if (!anyFieldHasError) {
          setWarning("");
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    // Prevent further input if current value already has 10 digits
    const currentValue = e.target.value.replace(/\D/g, "");
    if (currentValue.length >= 10 && /\d/.test(e.key)) {
      e.preventDefault();

      // Get field name from input
      const field = e.target.name || e.target.getAttribute("data-field");
      if (field) {
        setFieldExceedsMax((prev) => ({
          ...prev,
          [field]: true,
        }));
      }

      setWarning(translations[lang].maxDigitWarning);
    }
  };

  /* ================= FORM VALIDATION ================= */
  const validateForm = () => {
    const r = rows[0];

    // Check for max value violations
    const hasMaxValueErrors = Object.values(fieldExceedsMax).some(
      (value) => value === true
    );
    if (hasMaxValueErrors) {
      message.warning(translations[lang].validRangeWarning);
      return false;
    }

    // 🔴 MANDATORY FIELD VALIDATION
    const mandatoryFields = [
      "totalWorks",
      "totalFundReceived",
      "totalFundUsed",
      "completedWorks",
      "ongoingWorks",
      "notStartedWorks",
      "remarks",
    ];

    const hasEmptyField = mandatoryFields.some(
      (field) => !r[field] || r[field].toString().trim() === ""
    );

    if (hasEmptyField) {
      message.warning(translations[lang].mandatoryFieldsWarning);
      return false;
    }

    // Logical check: completed + ongoing + notStarted === totalWorks
    const total = parseInt(r.totalWorks || 0, 10);
    const completed = parseInt(r.completedWorks || 0, 10);
    const ongoing = parseInt(r.ongoingWorks || 0, 10);
    const notStarted = parseInt(r.notStartedWorks || 0, 10);
    if (completed + ongoing + notStarted !== total) {
      message.warning(translations[lang].sumMismatchWarning);
      return false;
    }

    // Logical check: fund used <= fund received
    const received = parseFloat(r.totalFundReceived || 0);
    const used = parseFloat(r.totalFundUsed || 0);
    if (used > received) {
      message.warning(translations[lang].fundExceedWarning);
      return false;
    }

    return true;
  };

  /* ================= SUBMIT / UPDATE CONFIRMATION DIALOG TRIGGER ================= */
  const handleSaveClick = () => {
    if (isFrozen) return;
    if (!validateForm()) return;
    setConfirmType("save");
    setOpenConfirmDialog(true);
  };

  const handleUpdateClick = () => {
    if (isFrozen) return;
    if (!validateForm()) return;
    setConfirmType("update");
    setOpenConfirmDialog(true);
  };

  /* ================= EXECUTE SAVE (1st time create) ================= */
  const executeSave = async () => {
    setOpenConfirmDialog(false);
    try {
      const r = rows[0];

      const dCode = district_code ? parseInt(district_code, 10) : undefined;
      const bCode = block_code ? parseInt(block_code, 10) : undefined;
      const gpCode = gram_panchayat_code ? parseInt(gram_panchayat_code, 10) : undefined;
      const mId = meeting_id ? parseInt(meeting_id, 10) : undefined;

      const payload = {
        userId,
        created_by: parseInt(userId || 0, 10),
        district_code: dCode,
        block_code: bCode,
        gram_panchayat_code: gpCode,
        panchayat_code: gpCode,
        sched_meet_id: mId,
        meeting_id: mId,
        total_executed_works: parseInt(r.totalWorks || 0, 10),
        total_received_amount: parseFloat(r.totalFundReceived || 0),
        total_utilized_amount: parseFloat(r.totalFundUsed || 0),
        completed_works: parseInt(r.completedWorks || 0, 10),
        ongoing_works: parseInt(r.ongoingWorks || 0, 10),
        not_started_works: parseInt(r.notStartedWorks || 0, 10),
        gram_sabha_remarks: r.remarks || "",
      };

      console.log("Meeting Data:", meetingData);
      console.log({
        district_code,
        block_code,
        gram_panchayat_code,
        meeting_id
      });
      console.log("Payload:", payload);

      await createDevelopmentWorkSummary(payload);
      setWarning("");
      setFieldExceedsMax({});
      await fetchDevelopmentWork();
      Swal.fire({
        icon: "success",
        title: translations[lang].savedSuccessfully,
        text: translations[lang].savedSuccessMsg,
        confirmButtonText: translations[lang].okay || "Okay",
      });
    } catch (err) {
      console.error(err);
      message.error(translations[lang].submitFailed);
    }
  };

  /* ================= EXECUTE UPDATE (1st edit -> permanent freeze) ================= */
  const executeUpdate = async () => {
    setOpenConfirmDialog(false);
    try {
      const r = rows[0];
      if (!r?.id) {
        message.error(translations[lang].noRecordFound);
        return;
      }

      const dCode = district_code ? parseInt(district_code, 10) : undefined;
      const bCode = block_code ? parseInt(block_code, 10) : undefined;
      const gpCode = gram_panchayat_code ? parseInt(gram_panchayat_code, 10) : undefined;
      const mId = meeting_id ? parseInt(meeting_id, 10) : undefined;

      const payload = {
        id: r.id,
        userId,
        updated_by: parseInt(userId || 0, 10),
        district_code: dCode,
        block_code: bCode,
        gram_panchayat_code: gpCode,
        panchayat_code: gpCode,
        sched_meet_id: mId,
        meeting_id: mId,
        total_executed_works: parseInt(r.totalWorks || 0, 10),
        total_received_amount: parseFloat(r.totalFundReceived || 0),
        total_utilized_amount: parseFloat(r.totalFundUsed || 0),
        completed_works: parseInt(r.completedWorks || 0, 10),
        ongoing_works: parseInt(r.ongoingWorks || 0, 10),
        not_started_works: parseInt(r.notStartedWorks || 0, 10),
        gram_sabha_remarks: r.remarks || "",
        is_edited: 1,
        is_freeze: 1,
      };

      await updateDevelopmentWorkSummary(payload);

      if (r.id) {
        localStorage.setItem(`dev_work_frozen_${r.id}`, "true");
      }
      if (gram_panchayat_code && meeting_id) {
        localStorage.setItem(`dev_work_frozen_${gram_panchayat_code}_${meeting_id}`, "true");
      }

      setIsSubmitted(true);
      setIsFrozen(true);
      setWarning("");
      setFieldExceedsMax({});
      await fetchDevelopmentWork();

      Swal.fire({
        icon: "success",
        title: translations[lang].updatedSuccessfully,
        text: translations[lang].updatedSuccessMsg,
        confirmButtonText: translations[lang].okay,
      });
    } catch (err) {
      console.error(err);
      message.error("Update failed");
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = () => {
    if (isFrozen) {
      Swal.fire({
        icon: "warning",
        title: translations[lang].confirmUpdateTitle,
        text: translations[lang].freezeNotice,
        confirmButtonText: translations[lang].okay,
      });
      return;
    }
    setIsSubmitted(false);
    setWarning("");
    setFieldExceedsMax({});
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (isFrozen) {
      Swal.fire({
        icon: "warning",
        title: translations[lang].confirmUpdateTitle,
        text: translations[lang].freezeNotice,
        confirmButtonText: translations[lang].okay,
      });
      return;
    }
    if (!rows[0]?.id) {
      message.warning(translations[lang].noRecordFound);
      return;
    }

    const result = await Swal.fire({
      title: translations[lang].deleteConfirmTitle,
      text: translations[lang].deleteConfirmMsg,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translations[lang].yesDelete,
      cancelButtonText: translations[lang].cancel,
    });

    if (!result.isConfirmed) return;

    try {
      if (rows[0]?.id) {
        localStorage.removeItem(`dev_work_frozen_${rows[0].id}`);
      }
      if (gram_panchayat_code && meeting_id) {
        localStorage.removeItem(`dev_work_frozen_${gram_panchayat_code}_${meeting_id}`);
      }
      await deleteDevelopmentWorkSummary(rows[0].id);
      setRows([{ ...emptyRow }]);
      setIsSubmitted(false);
      setIsFrozen(false);
      setWarning("");
      setFieldExceedsMax({});
      message.success(translations[lang].recordDeletedSuccess);
    } catch (err) {
      console.error(err);
      message.error(translations[lang].deleteFailed);
    }
  };

  /* ================= EXPORT BLANK PDF FORM ================= */
  const handleExportPDF = () => {
    try {
      const currentDate = new Date().toLocaleDateString("hi-IN");

      // Create HTML content for PDF
      const element = document.createElement("div");
      element.innerHTML = `
        <div style="
  font-family: Arial, sans-serif;
  width: 794px;
  min-height: 1122px;
  padding: 25px 30px;
  margin: 0 auto;
  box-sizing: border-box;
  background: white;
  overflow: hidden;
">

          <div style="text-align: center; margin-bottom: 15px; border-bottom: 2px solid #2E4A8E; padding-bottom: 15px;">
            <h1 style="color: #2E4A8E; margin-bottom: 10px; font-size: 24px;">विकास कार्य सारांश फॉर्म (2025-26)</h1>
            <h2 style="color: #333; margin-bottom: 5px; font-size: 18px;">ग्राम सभा विकास कार्य रिपोर्ट</h2>
          </div>
          
          <table style="width:100%; margin-bottom:25px;">
  <tr>
    <td><strong>पंचायत:</strong> ..............</td>
    <td style="text-align:right;"><strong>तारीख:</strong> ${currentDate}</td>
  </tr>
</table>

          
          <div style="background-color: #f9f9f9; padding: 15px; margin-bottom: 20px; border-left: 3px solid #2E4A8E;">
            <p style="margin: 0; font-style: italic; color: #666; font-size: 13px;">
              नोट: कृपया निम्नलिखित फॉर्म को ध्यान से भरें। सभी जानकारी सही और पूर्ण होनी चाहिए।
            </p>
          </div>
          
         <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; border: 1px solid #000; font-size:13px;">

            <thead>
              <tr style="background-color: #2E4A8E; color: white;">
                <th style="padding: 8px; border: 1px solid #000; text-align: center; width: 5%;">क्र.सं.</th>
                <th style="padding: 8px; border: 1px solid #000; text-align: left; width: 60%;">विवरण</th>
                <th style="padding: 8px; border: 1px solid #000; text-align: center; width: 35%;">मूल्य (भरें)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">1.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">सभी योजनाओं के तहत 2025-26 के भीतर निष्पादित विकास कार्यों की कुल संख्या</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  <div style="border-bottom: 1px solid #000; display: inline-block; width: 120px; height: 20px;"></div>
                </td>
              </tr>
              <tr style="background-color: #f5f5f5;">
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">2.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">इन कार्यों के लिए 2025-26 के दौरान कुल प्राप्त/संयोजित धनराशि</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  ₹ <div style="border-bottom: 1px solid #000; display: inline-block; width: 100px; height: 20px; margin-left: 5px;"></div>
                </td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">3.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">इन कार्यों पर 2025-26 के दौरान कुल धनराशि का उपयोग किया गया</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  ₹ <div style="border-bottom: 1px solid #000; display: inline-block; width: 100px; height: 20px; margin-left: 5px;"></div>
                </td>
              </tr>
              <tr style="background-color: #f5f5f5;">
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">4.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">पूर्ण किए गए कार्यों की संख्या</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  <div style="border-bottom: 1px solid #000; display: inline-block; width: 120px; height: 20px;"></div>
                </td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">5.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">चल रहे कार्यों की संख्या</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  <div style="border-bottom: 1px solid #000; display: inline-block; width: 120px; height: 20px;"></div>
                </td>
              </tr>
              <tr style="background-color: #f5f5f5;">
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">6.</td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top;">शुरू नहीं किए गए कार्यों की संख्या</td>
                <td style="padding: 8px; border: 1px solid #000; text-align: center; vertical-align: top;">
                  <div style="border-bottom: 1px solid #000; display: inline-block; width: 120px; height: 20px;"></div>
                </td>
              </tr>
            </tbody>
          </table>
          
          <div style="margin-top: 30px;">
            <div style="font-weight: bold; color: #2E4A8E; margin-bottom: 15px; font-size: 16px;">7. ग्राम सभा के सदस्यों की टिप्पणियां:</div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
            <div style="border-bottom: 1px solid #666; margin: 12px 0; height: 20px;"></div>
          </div>
          
          <div style="display: flex; justify-content: space-between; margin-top: 35px;">
            <table style="width:100%; margin-top:60px;">
  <tr>
    <td style="width:45%; text-align:center;">
      ग्राम सभा प्रतिनिधि<br>
      <div style="border-top:1px solid #000; margin-top:18px;">हस्ताक्षर</div>
    </td>
    <td style="width:45%; text-align:center;">
      पंचायत सचिव<br>
      <div style="border-top:1px solid #000; margin-top:18px;">हस्ताक्षर</div>
    </td>
  </tr>
</table>

          </div>
          
          <div style="text-align: center; margin-top: 50px; font-size: 11px; color: #666; border-top: 1px solid #ddd; padding-top: 15px;">
            <p style="margin: 5px 0;">यह फॉर्म विकास कार्य सारांश के लिए है। कृपया भरे हुए फॉर्म को संबंधित कार्यालय में जमा करें।</p>
            <p style="margin: 5px 0;">स्वचालित रूप से जनरेट की गई फॉर्म | ${currentDate}</p>
          </div>
        </div>
      `;

      // Configure PDF options
      const opt = {
        margin: 0,
        filename: `विकास_कार्य_फॉर्म_${currentDate.replace(/\//g, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      };

      // Generate and download PDF
      html2pdf().set(opt).from(element).save();

      message.success("PDF फॉर्म डाउनलोड हो रहा है...");
    } catch (error) {
      console.error("PDF export error:", error);
      message.error("PDF बनाने में त्रुटि");
    }
  };

  /* ================= UI ================= */
  return (
    <div style={{ padding: 16 }}>
      {/* Back Button, PDF Export Button, and Language Converter Toggle */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button variant="outlined" onClick={() => navigate("/gram-sabha-inspection")}>
          {translations[lang].back}
        </Button>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleExportPDF}
            sx={{ backgroundColor: "#4CAF50" }}
          >
            {translations[lang].downloadPdf}
          </Button>

          <Button
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            variant="outlined"
            style={{ textTransform: "none", color: "#4763E4", borderColor: "#4763E4", fontWeight: "bold" }}
          >
            {translations[lang].langToggle}
          </Button>
        </Stack>
      </Stack>

      <h3 style={{ color: "#2E4A8E" }}>{translations[lang].title}</h3>


      {/* Warning Message Display */}
      {warning && (
        <div
          style={{
            backgroundColor: "#ffebee",
            border: "2px solid #f44336",
            borderRadius: "4px",
            padding: "8px 12px",
            marginBottom: "16px",
            color: "#c62828",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "18px", color: "#f44336" }}>⚠️</span>
          <span>{warning}</span>
        </div>
      )}

      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table size="small" sx={{ border: "1px solid #ccc", minWidth: "1200px", pointerEvents: isFrozen ? "none" : "auto" }}>
          <TableHead>
          <TableRow sx={{ backgroundColor: "#2E4A8E" }}>
            {[
              translations[lang].colSrNo,
              translations[lang].col1,
              translations[lang].col2,
              translations[lang].col3,
              translations[lang].col4,
              translations[lang].col5,
              translations[lang].col6,
              translations[lang].col7,
              translations[lang].colAction,
            ].map((h, i) => (
              <TableCell
                key={i}
                sx={{ 
                  color: "#fff", 
                  fontSize: 12, 
                  textAlign: "center",
                  minWidth: i === 0 ? "60px" : i === 8 ? "120px" : "200px",
                  maxWidth: i === 0 ? "60px" : i === 8 ? "120px" : "200px",
                  whiteSpace: "normal",
                  wordWrap: "break-word"
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell align="center">{index + 1}</TableCell>

              {[
                "totalWorks",
                "totalFundReceived",
                "totalFundUsed",
                "completedWorks",
                "ongoingWorks",
                "notStartedWorks",
              ].map((field) => (
                <TableCell key={field}>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    disabled={isSubmitted || isFrozen}
                    value={row[field]}
                    name={field}
                    error={fieldExceedsMax[field]}
                    inputProps={{
                      maxLength: 10,
                      style: {
                        textAlign: "center",
                        fontFamily: "monospace",
                        letterSpacing: "0.5px",
                        backgroundColor: fieldExceedsMax[field]
                          ? "#ffebee"
                          : "inherit",
                      },
                    }}
                    onKeyDown={(e) => {
                      blockInvalidNumberInput(e);
                      handleKeyPress(e);
                    }}
                    onChange={(e) => handleChange(index, field, e.target.value)}
                    onFocus={() => setWarning("")}
                    onBlur={(e) => handleBlur(field, e.target.value)}
                    sx={{
                      minWidth: "120px",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: fieldExceedsMax[field]
                            ? "#f44336"
                            : "rgba(0, 0, 0, 0.23)",
                          borderWidth: fieldExceedsMax[field] ? "2px" : "1px",
                        },
                        "&:hover fieldset": {
                          borderColor: fieldExceedsMax[field]
                            ? "#f44336"
                            : "rgba(0, 0, 0, 0.87)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: fieldExceedsMax[field]
                            ? "#f44336"
                            : "#1976d2",
                          borderWidth: fieldExceedsMax[field] ? "2px" : "2px",
                        },
                      },
                    }}
                  />
                </TableCell>
              ))}

              <TableCell>
                <Tooltip 
                  title={row.remarks || ""} 
                  arrow 
                  placement="bottom" 
                  disableHoverListener={!row.remarks}
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: '#ffffff !important', color: '#000000 !important', border: '1px solid #ccc', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', fontSize: '13px' } },
                    arrow: { sx: { color: '#ffffff !important' } }
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isSubmitted || isFrozen}
                    value={row.remarks}
                    inputProps={{
                      style: {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                    onChange={(e) => {
                      const updated = [...rows];
                      updated[index].remarks = e.target.value;
                      setRows(updated);
                    }}
                    onFocus={() => setWarning("")}
                  />
                </Tooltip>
              </TableCell>

              <TableCell align="center">
                <Stack direction="row" spacing={1}>
                  <Tooltip
                    title={
                      isFrozen
                        ? translations[lang].frozenTooltip
                        : ""
                    }
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!isSubmitted || isFrozen}
                        onClick={handleEdit}
                      >
                        {isFrozen ? translations[lang].frozen : translations[lang].edit}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={!isSubmitted || isFrozen || !rows[0]?.id}
                    onClick={handleDelete}
                  >
                    {translations[lang].delete}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: "10px" }}>
        {!isSubmitted && !isFrozen ? (
          rows[0]?.id ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateClick}
            >
              {translations[lang].update}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveClick}
            >
              {translations[lang].submit}
            </Button>
          )
        ) : null}
      </div>

      {/* Confirmation Dialog Popup */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle
          id="confirm-dialog-title"
          style={{ fontWeight: "bold", color: "#2E4A8E" }}
        >
          {confirmType === "save" ? translations[lang].confirmSaveTitle : translations[lang].confirmUpdateTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-dialog-description"
            style={{ color: "#333", fontSize: "15px" }}
          >
            {confirmType === "save"
              ? translations[lang].confirmSaveMsg
              : translations[lang].confirmUpdateMsg}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "15px 24px" }}>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            variant="outlined"
            style={{
              textTransform: "none",
              color: "#666",
              borderColor: "#ccc",
            }}
          >
            {translations[lang].cancel}
          </Button>
          <Button
            onClick={confirmType === "save" ? executeSave : executeUpdate}
            variant="contained"
            color="primary"
            autoFocus
            style={{ textTransform: "none", backgroundColor: "#2E4A8E" }}
          >
            {confirmType === "save" ? translations[lang].yesSave : translations[lang].yesUpdate}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DevelopmentWork;